import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import {
  formatStepKey,
  parseStepKey,
} from '@/components/Event/NewScript/utils/stepKey'
import { ScriptStep, ScriptStepStatus } from '@/constants'
import { Event, ScriptData } from '@/types'

const stepStatusLabels = {
  [ScriptStepStatus.IN_PROGRESS]: 'In progress',
  [ScriptStepStatus.COMPLETED]: 'Completed',
  [ScriptStepStatus.INCOMPLETE]: 'Incomplete',
  [ScriptStepStatus.SKIPPED]: 'Skipped',
}

type NavigationStepItem = {
  key: string
  label: string
  status: ScriptStepStatus
  offset: boolean
}

const singleWitnessSteps = [
  { key: formatStepKey(ScriptStep.PREPARATION), label: '1. Preparation' },
  { key: formatStepKey(ScriptStep.ATTENDANCE, 0), label: '2. Attendance' },
  { key: formatStepKey(ScriptStep.OPENING, 0), label: '3. Opening' },
  { key: formatStepKey(ScriptStep.QUESTIONING, 0), label: '4. Questioning' },
  { key: formatStepKey(ScriptStep.CLOSING, 0), label: '5. Closing' },
  { key: formatStepKey(ScriptStep.REVIEW), label: '6. Review' },
]

const getMultiWitnessStepLabel = (
  stepKey: ScriptStep,
  witnessIndex: string | undefined,
  eventCount: number,
): string => {
  if (stepKey === ScriptStep.PREPARATION) {
    return '1. Preparation'
  }

  if (stepKey === ScriptStep.REVIEW) {
    return `${eventCount + 2}. Review`
  }

  if (!witnessIndex) {
    throw new Error(
      `Missing witness index for step "${stepKey}" in multi-witness script`,
    )
  }

  const witnessNumber = Number(witnessIndex) + 1
  const sectionNumber = witnessNumber + 1

  switch (stepKey) {
    case ScriptStep.ATTENDANCE:
      return `${sectionNumber}. Attendance (Witness ${witnessNumber})`
    case ScriptStep.OPENING:
      return `${sectionNumber}.1 Opening`
    case ScriptStep.QUESTIONING:
      return `${sectionNumber}.2 Questioning`
    case ScriptStep.CLOSING:
      return `${sectionNumber}.3 Closing`
    default:
      throw new Error(
        `Unexpected step "${stepKey}" for witness ${witnessIndex}`,
      )
  }
}

export const getScriptNavigationItems = (
  scriptData: ScriptData,
): NavigationStepItem[] => {
  if (scriptData.events.length === 1) {
    return singleWitnessSteps.map(({ key, label }) => ({
      key,
      label,
      status: scriptData.steps[key].status,
      offset: false,
    }))
  }

  return Object.entries(scriptData.steps).map(([key, step]) => {
    const { step: stepKey, witnessIndex } = parseStepKey(key)

    return {
      key,
      label: getMultiWitnessStepLabel(
        stepKey,
        witnessIndex,
        scriptData.events.length,
      ),
      status: step.status,
      offset: !!witnessIndex && stepKey !== ScriptStep.ATTENDANCE,
    }
  })
}

export const useStepsNavigation = (_event: Event) => {
  const { state, actions } = useScriptState()

  const getStatus = (step: string): ScriptStepStatus => {
    return state.steps[step].status
  }

  const getStatusLabel = (step: string): string => {
    return stepStatusLabels[getStatus(step)]
  }

  const isInProgress = (step: string): boolean => {
    return getStatus(step) === ScriptStepStatus.IN_PROGRESS
  }

  const isIncomplete = (step: string): boolean => {
    return getStatus(step) === ScriptStepStatus.INCOMPLETE
  }

  const isCompleted = (step: string) => {
    return getStatus(step) === ScriptStepStatus.COMPLETED
  }

  const isSkipped = (step: string) => {
    return getStatus(step) === ScriptStepStatus.SKIPPED
  }

  const setCurrentStep = (step: string) => {
    actions.updateCurrentStep(step)
  }

  const getNavColor = (step: string): string | undefined => {
    if (isInProgress(step)) {
      return 'blue'
    } else if (isCompleted(step)) {
      return 'green'
    } else if (isSkipped(step)) {
      return 'gray'
    } else {
      return undefined
    }
  }

  const scriptSteps = getScriptNavigationItems(state as ScriptData)

  return {
    getStatus,
    isInProgress,
    isIncomplete,
    isCompleted,
    setCurrentStep,
    getNavColor,
    getStatusLabel,
    scriptSteps,
  }
}
